/**
 * Transaction Collection View
 * @author tbroussard
 *
 * This view initializes each transaction item view and handles behaviors for
 * toggling display modes and executing filter searches.
 */
define([
	'underscore',
	'lib/wurfl',
	'backbone',
	'BaseView',
	'view/activity/transactionView',
	'lib/fpti-utils',
	'jquery'
], function (_, wurfl, Backbone, BaseView, TransactionView, FPTIUtils, $) {

	'use strict';

	var TransactionCollectionView = BaseView.extend({

		el: '#js_transactionCollection',

		template: 'activity/transactionCollectionSimple',

		events: {
			'click .js_retryRequest': 'retryRequest'
		},

		activeTransactionViews: [],

		/*
		 * Initialize Transaction Collection View
		 */
		initialize: function (params) {
			this.viewContext = params.viewContext || '';
			this.trackFPTILink = FPTIUtils.createLinkTracker(
				'main:walletweb:' + this.viewContext
			);
			this.country = params.country || '';
			this.error = params.error || null;

			this.collection.hasTransactionsPending = params.hasTransactionsPending || false;
			this.collection.hasTransactionsCompleted = params.hasTransactionsCompleted || false;
			this.collection.hasFiltersSet = false;
			this.collection.isMerchantCategoryEnabled = params.isMerchantCategoryEnabled || false;
			this.collection.noOfTransactions = params.noOfTransactions || 0;
			this.collection.statementLinks = params.statementLinks || [];
			this.collection.showLoadMore = params.showLoadMore || false;
			this.collection.nextPageToken = params.nextPageToken || '';
			this.collection.currencyCodes = params.currencyCodes || [];

			// Set view template on initialize for Activity view only
			// TODO: Needs to be done with DisplayModel
			if (params.showDetailedView && this.viewContext === 'activity') {
				this.setViewTemplate('detailed');
			}

			this.listenTo(this.collection, 'reset', this.render);

			// TODO: Needs to be done with DisplayModel.
			// - Watching model data change connects the action to change in some managed state.
			// - Listening for Backbone event to trigger is unconnected to any state and relies on static data param
			this.listenTo(Backbone, 'displayMode:change', this.setViewTemplate);

			this.listenTo(Backbone, 'filter:doSearch', this.loadTransactions);

			// Transaction item expand/collapse events
			// TODO can this listen for model state changes instead of events?
			this.listenTo(Backbone, 'transactionItem:toggleLoading', this.toggleTransactionLoading);
			// because the fallback transition events cause this event to
			// fire multiple times, we get trash FPTI data, so debounce it
			this.listenTo(
				Backbone,
				'transactionItem:toggleExpanded',
				_.debounce(this.toggleTransactionExpanded, 500)
			);

			// Handle caching of transaction collection subviews
			// TODO can this listen for model state changes instead of events?
			this.listenTo(Backbone, 'transactionItem:viewRendered', this.cacheTransactionView);

			// Handle hide/show of inline transaction details
			// TODO can this listen for model state changes instead of events?
			this.listenTo(Backbone, 'transactionDetails:toggleRequestedToShow', this.toggleTransactionDetails);

			if (params.freeTextSearch) {
				// Execute a free text search via query param before
				// initializing the Transaction and Transaction Details Views
				this.loadTransactions({
					freeTextSearch: params.freeTextSearch,
					isClientSideFiltering: true,
					selectedCurrency: 'ALL'
				});
			} else {
				// Initialize the Transaction and Transaction Details Views
				this.renderCollectionSubViews();
			}
		},

		/*
		 * This calls a fetch on the collection for a new search or additional
		 * (load more) transaction data.
		 *
		 * @param params {Object} - Search parameters (i.e, transaction type, start/end dates)
		 */
		loadTransactions: function (params) {
			var self = this,
				filterModel;

			// Show spinner animation while the search processes
			this.toggleSpinner(true);

			// make sure to reset this flag everytime we load new transactions
			this.collection.hasFiltersSet = false;

			if (params.isClientSideFiltering && this.collection.models) {
				return this.transactionSearch(params);
			}

			// Pass search parameters to this collection's load method to fetch transaction data
			if (params) {
				this.collection.load(params);
				filterModel = self.collection.filterModel;

				if (filterModel.has('isDesktopView') && filterModel.get('isDesktopView')) {
					// Reset filterModel context after server call

					if (filterModel.has('_freeTextDateSearch')) {
						filterModel.set('freeTextSearch', filterModel.get('_freeTextDateSearch'), { silent: true });
						filterModel.unset('_freeTextDateSearch', { silent: true });
					}

					if (filterModel.has('_transactionFilterDateSearch')) {
						filterModel.set('transactionFilter', filterModel.get('_transactionFilterDateSearch'), { silent: true });
						filterModel.unset('_transactionFilterDateSearch', { silent: true });
					}

					filterModel.set('isClientSideFiltering', true, { silent: true });

					Backbone.listenToOnce(this.collection, 'transactionCollection:newSearch', function() {
						self.transactionSearch({
							freeTextSearch: filterModel.get('freeTextSearch'),
							selectedCurrency: filterModel.get('selectedCurrency'),
							transactionFilter: filterModel.get('transactionFilter')
						});
					});
				}
			}
		},

		/*
		 * Checks the filter attributes (search/advance filters/currency selector) and returns true
		 * if none of the filters are set (transactions are not being filtered) or false otherwise.
		 *
		 * @param params {Object} - Search parameters (i.e, transaction type, start/end dates)
		 */
		hasFiltersSet: function (params) {
			var searchTerm = params.freeTextSearch && params.freeTextSearch.toLowerCase(),
				selectedFilter = params.transactionFilter,
				selectedCurrency = params.selectedCurrency;

			var isSearchTermEntered = (searchTerm !== undefined) && (searchTerm !== ''),
				isFilterSelected = selectedFilter !== undefined,
				isCurrencySelected = selectedCurrency !== 'ALL';

			return isSearchTermEntered || isFilterSelected || isCurrencySelected;
		},

		/*
	 	* This function handles transaction search (free text search and transaction type filter).
	 	*
	 	* @param params {Object} - Search parameters (i.e, transaction type, start/end dates)
	 	*/
		transactionSearch: function (params) {
			var data = this.collection.models,
				searchTerm = params.freeTextSearch && params.freeTextSearch.toLowerCase(),
				selectedFilter = params.transactionFilter,
				selectedCurrency = params.selectedCurrency;

			this.collection.hasFiltersSet = this.hasFiltersSet(params);

			// if no transactions, then exit early.
			if (data.length === 0) {
				this.updateCollection(data);
				this.render();
			}

			// Bring back all activity view with all the transactions when none of the filters are selected and free text search is empty.
			if (params.isClearFreeTextSearch || params.isClearFilterSelection) {
				this.setShouldShow(data);
			}

			// Transaction type filter search
			if (selectedFilter) {
				this.transactionFilterSearch(data, selectedFilter);
			} else {
				this.setShouldShow(data);
			}

			/*
			For the combined search ( transaction filter search + free text search), we are first executing txn filter search
			and then executing free text search.
			 */
			// Free text search
			if (searchTerm) {
				this.freeTextSearch(data, searchTerm);
			}

			// Filter txns by currency if we don't want to show all currency types
			if (selectedCurrency !== 'ALL') {
				this.currencyFilter(data, selectedCurrency);
			}

			// trigger change to make sure that load more view checks if load more should show
			this.collection.trigger('change', this);
			this.updateCollection(data);

			this.render();
		},

		// Get the property value from data object.
		getFieldValue: function(data, searchField) {
			return searchField.split('.').reduce(function(prev, curr) {
				return (typeof prev === 'undefined' || prev === null) ? prev : prev[curr];
			}, data);
		},

		/* This function handles free text search.
		 * If match is found, set shouldShow property to true. Else set shouldShow to false.
		 *
		 * Free text search is done on counterparty, txn ID, note, amount and type fields
		 */
		freeTextSearch: function(data, searchTerm) {
			var length = data.length,
				searchResultCount = 0,
				shouldShow,
				searchFields = ['counterparty',
								'displayType',
								'id',
								'amounts.formattedAmount.value',
								'merchantCategory',
								'freetextSearchData.notes',
								'freetextSearchData.memo',
								'freetextSearchData.email',
								'freetextSearchData.date.short',
								'freetextSearchData.date.medium',
								'freetextSearchData.date.long',
								'freetextSearchData.date.withFullYear',
								'freetextSearchData.invoiceId'];

			for (var i = 0; i < length; i++) {

				shouldShow = false;

				if (data[i].get('shouldShow')) { // Run free text search only for the transaction for which shouldShow property is true.
					var dataAttributes = _.clone(data[i].attributes);

					for (var j = 0; j < searchFields.length; j++) {
						var value = this.getFieldValue(dataAttributes, searchFields[j]);

						if (value) {
							if (value.toLowerCase().indexOf(searchTerm) > -1) {
								shouldShow = true;
								searchResultCount++;
								break;
							}
						}
					}

					// set shouldShow property to false since match is not found.
					if (!shouldShow) {
						data[i].set('shouldShow', false);
					}
				}
			}

			if (searchResultCount === 0) {
				Backbone.trigger('trackLink', null, 'freeTextSearchResultNull', 'main:walletweb:activity:search:searchresultnull:');
			}
		},

		/* Currency Filter
		 *
		 * Goes through data and if currency code attribute matches the selected currency code then we set the 'shouldShow'
		 * property to display the transaction
		 */
		currencyFilter: function(data, selectedCurrency) {
			var length = data.length;

			for (var i = 0; i < length; i++) {
				if (data[i].get('shouldShow')) {
					var txnDataAttributes = _.clone(data[i].attributes),
						txnCurrencyCode = this.getFieldValue(txnDataAttributes, 'amounts.currencyCode'),
						shouldShowTxn = txnCurrencyCode === selectedCurrency;

					// set shouldShow property to false since match is not found.
					if (!shouldShowTxn) {
						data[i].set('shouldShow', false);
					}
				}
			}
		},

		/* This function handles transaction type search (Payment Sent, refund, payment received and PAD).
		 * If match is found, set shouldShow property to true. Else set shouldShow to false.
		 *
		 */
		transactionFilterSearch: function(data, selectedFilter) {
			var length = data.length;

			for (var i = 0; i < length; i++) {
				if (data[i].get('filterSet')) {
					if (data[i].get('filterSet').indexOf(selectedFilter) > -1) {
						data[i].set('shouldShow', true);
					} else {
						data[i].set('shouldShow', false);
					}
				}
			}
		},

		/*
		 * This function brings back all activity view by setting "shouldShow" property to TRUE for all the transactions.
		 *
		 * @param data {Object} - List of transactions.
		 */
		setShouldShow: function (data) {
			var length = data.length;

			for (var i = 0; i < length; i++) {
				data[i].set('shouldShow', true);
			}
		},

		/*
		 * This function is called after client side filtering is done. This updates noOfTransactions, hasPending and hasCompleted properties in the collection.
		 *
		 * @param data {Object} - List of transactions.
		 */
		updateCollection: function(data) {
			var length = data.length,
				hasPending = false,
				hasCompleted = false,
				noOfTransactions = 0;

			for (var i = 0; i < length; i++) {

				if (data[i].get('transactionDetailsModel')) {
					data[i].unset('transactionDetailsModel');
				}

				if (data[i].get('shouldShow')) {
					var status = data[i].get('status');

					noOfTransactions += 1;

					if (!hasPending) {
						hasPending = this.isPending(status);
					}

					if (!hasCompleted) {
						hasCompleted = this.isCompleted(status);
					}
				}
			}
			this.collection.hasTransactionsPending = hasPending;
			this.collection.hasTransactionsCompleted = hasCompleted;
			this.collection.noOfTransactions = noOfTransactions;
			this.collection.messageModel.queueMessageFromResults({
				noOfTransactions: noOfTransactions
			});

		},

		/*
		 * This function returns TRUE if passed status key falls in PENDING bucket.
		 *
		 * @param status - transaction status.
		 */
		isPending: function(status) {
			var pendingStatuses = ['PENDING', 'HELD', 'UNCLAIMED', 'PARTIALLY_PAID'];

			return pendingStatuses.indexOf(status) > -1;
		},

		/*
		 * This function returns TRUE if passed status key falls in COMPLETED bucket.
		 *
		 * @param status - transaction status.
		 */
		isCompleted: function(status) {
			var completedStatuses = ['BLOCKED', 'CANCELED', 'COMPLETED', 'DENIED', 'FAILED', 'REFUNDED'];

			return completedStatuses.indexOf(status) > -1;
		},

		toggleSpinner: function (isToggledOn) {
			this.$el.toggleClass('hasSpinner', isToggledOn);
		},

		/*
		 * Override BaseView's serialize function with one that makes more
		 * sense for the context of the activity collection.
		 *
		 * TODO: Should just inject data from TransactionCollection and
		 * ActivityModel, the way errors are being done.
		 *
		 * TODO: Should return JSON
		 */
		serialize: function() {

			// REVIEW: This literal object needs to not be done this way, and
			// collection.data should not exist
			var results = {
				data: this.collection.data || {},
				viewContext: this.viewContext,
				country: this.country,
				hasFiltersSet: this.collection.hasFiltersSet,
				transactions: this.collection.toJSON(),
				hasTransactionsPending: this.collection.hasTransactionsPending,
				hasTransactionsCompleted: this.collection.hasTransactionsCompleted,
				isMerchantCategoryEnabled: this.collection.isMerchantCategoryEnabled,
				noOfTransactions: this.collection.noOfTransactions,
				statementLinks: this.collection.statementLinks,
				showLoadMore: this.collection.showLoadMore,
				currencyCodes: this.collection.currencyCodes
			};

			/* Injection magic. Get data from Collection and ActivityModel to
			 * emulate structure of server-side view model data */
			results.data.activity = results.data.activity || {};

			// hasError returns the type of error
			results.data.activity.error = this.collection.activityModel.hasError();

			return results;
		},

		/**
		 * After rendering Transaction Collection View, render its sub-views.
		 */
		afterRender: function () {
			this.renderCollectionSubViews();
			this.toggleSpinner(false);

			// Show queued message if there one and there are filters
			// Note: messageModel does not exist for Summary page
			if (this.collection.messageModel && !this.collection.activityModel.hasError() && this.collection.hasFiltersSet) {
				this.collection.messageModel.checkToShowMessage();
			} else if (this.collection.messageModel) {
				this.collection.messageModel.clearMessage();
			}

			// hide/show load more after collection renders
			if (this.$el.siblings('.js_loadMore-container')) {
				if (this.collection.length === 0) {
					this.$el.siblings('.js_loadMore-container').addClass('hide'); // Do not show load more if no txns are shown.
				} else {
					this.$el.siblings('.js_loadMore-container').removeClass('hide');
				}
			}

		},

		/**
		 * Retry request after error response from service (user-invoked)
		 */
		retryRequest: function (event) {
			event.preventDefault();

			var params = {};

			// Show spinner animation while the page reload processes
			this.toggleSpinner(true);

			if (this.collection.filterModel) {
				params = this.collection.filterModel.getSearchParameters();
			}

			params.isNewSearch = true;

			// Fetch transaction data
			this.collection.load(params);
		},

		/**
		 * Handle Display Model change events.
		 * This triggers re-render of Transaction Collection View with the
		 * template corresponding to the selected Display 'mode' setting. All
		 * sub-Views in this collection will subsequently re-render via
		 * this.afterRender()
		 *
		 * @param displayMode {String} - The name of the current display mode setting
		 */
		setViewTemplate: function (displayMode) {
			if (displayMode === 'detailed') {
				this.template = 'activity/transactionCollectionDetailed';
			} else {
				this.template = 'activity/transactionCollectionSimple';
			}

			/**
			 * Set display mode on collection view so we can pass it down to the Transaction View
			 * when sub-views are rendered. This is done because the Transaction View is unaware
			 * of the display mode setting when it's instantiated and rendered by this collection.
			*/
			this.displayMode = displayMode;

			// Render if there is no service error (because render might erase
			// the error message)
			if (!this.collection.activityModel.hasError()) {
				this.render();
			}
		},

		/*
		 * Handle rendering of Transaction Collection sub-Views.
		 * This instantiates each Transaction View with the correct template based on the
		 * Display 'mode' setting and sets any needed model properties on each Transaction Model
		 * before rendering the Transaction View.
		 */
		renderCollectionSubViews: function () {

			// Clear listeners and memory of existing views
			_.invoke(this.activeTransactionViews, 'remove');
			this.activeTransactionViews.length = 0;

			// Instantiate and render each Transaction View in the collection
			this.collection.each(function (transactionModel) {
				transactionModel.set('viewContext', this.viewContext);

				if (transactionModel.get('transactionDetailsModel')) {
					transactionModel.unset('transactionDetailsModel');
				}

				// Instantiate each Transaction View in this collection
				var transactionView = new TransactionView({
					viewContext: this.viewContext,
					model: transactionModel,
					displayMode: this.displayMode
				});

				// Pass wurfl object so Transaction Model can evaluate whether to support inline details
				transactionModel.set('wurfl', wurfl);

				// Pass flag to check if category is availble to Transaction Model
				transactionModel.set('isMerchantCategoryEnabled', this.collection.isMerchantCategoryEnabled);

				transactionView.render();

			}, this);
		},

		/*
		 * Handle TransactionView:viewRendered and DisplayView.afterRender() events.
		 * This updates the DOM with Transaction Views after they are asynchronously rendered.
		 */
		cacheTransactionView: function (transactionView) {
			var docFragPending = document.createDocumentFragment(),
				docFragCompleted = document.createDocumentFragment();

			// Populate cache with the rendered item view
			this.activeTransactionViews.push(transactionView);

			// Append transaction views to the collection view
			if (this.activeTransactionViews.length === this.collection.length) {

				// Empty the transaction list container
				if (this.displayMode === 'detailed') {
					// Remove only tbody elements to retain table headers
					this.$el.find('.js_transactionList tbody').remove();
				} else {
					this.$el.find('.js_transactionList').empty();
				}

				// Append transaction groups to their respective containers
				_.each(this.activeTransactionViews, function (subview) {

					// Append sub-views to document fragment (lightweight; better performance)
					// This builds sub-views in memory and later appends them to the DOM once
					// per txn status, resulting in only 2 reflows of the DOM - win!
					if (subview.model.get('status') === 'PENDING') {
						// iterate subview element(s) incase we have a year divider to append,
						// otherwise this will just append the single transaction item node
						_.each(subview.$el, function(el) {
							docFragPending.appendChild(el);
						});
					} else {
						_.each(subview.$el, function(el) {
							docFragCompleted.appendChild(el);
						});
					}

				}, this);

				// Finally append the fragments to the DOM
				this.$el.find('.js_pendingModule .js_transactionList').append(docFragPending);
				this.$el.find('.js_completedModule .js_transactionList').append(docFragCompleted);
			}
		},

		/**
		 * Toggle expansion of Transaction Details View
		 *
		 * @param transactionId {String} - The ID of the target transaction
		 * @param toShow {Boolean} - True or false for expanding transaction details
		 */
		toggleTransactionDetails: function (transactionId, toShow) {
			this.collection.get(transactionId).get('transactionDetailsModel').toggleRequestedToShow(toShow);
		},

		/*
		 * Handle events triggered when inline Transaction Details is loading.
		 * This provides ability to display loading indicator while the data loads.
		 *
		 * @param transactionId {String} - The ID of the target transaction
		 * @param isLoading {Boolean} - True or false for loading the target transaction
		 */
		toggleTransactionLoading: function (transactionId, isLoading) {
			var transactionItem = this.$el.find('.js_transactionItem-' + transactionId),
				transactionDetails = transactionItem.find('#transactionDetails-' + transactionId),
				spinnerLabelText = transactionItem.find('#js_spinnerLabelText-' + transactionId).text(),
				spinnerId = 'js_spinner-txnId_' + transactionId,
				spinnerEl;

			if (isLoading === false) {
				this.$el.find('#' + spinnerId).remove();
				transactionDetails.attr('aria-busy', false);
			} else {

				spinnerEl = this.createSpinner({
					transactionId: transactionId,
					spinnerId: spinnerId,
					spinnerLabelText: spinnerLabelText
				});

				this.$el.append(spinnerEl);
				this.$el.find('#' + spinnerId).focus();
				transactionDetails.attr('aria-busy', true);
			}

			// prevent tabbing when txn is loading
			this.togglePreventTabbing(isLoading);

			this.collection.get(transactionId).toggleProp('isLoading', isLoading);
		},

		/**
		 * Prevents user from tabbing
		 *
 		 * @param preventTabbing {Boolean} - True or false preventing tabbing on the page
		 */
		togglePreventTabbing: function(preventTabbing) {
			if (preventTabbing === false) {
				$('body').off('keydown');
			} else {
				$('body').on('keydown', function(event) {
					if (event.keyCode && event.keyCode === 9) {
						event.preventDefault();
					}
				});
			}
		},

		/**
		 * Create new spinner element with accessible attributes
		 *
		 * @param params {Object} - An object of attributes used to construct spinner element
		 */
		createSpinner: function (params) {
			var transactionItem = this.$el.find('.js_transactionItem-' + params.transactionId),
				spinnerEl = document.createElement('div');

			spinnerEl.setAttribute('id', params.spinnerId);
			spinnerEl.setAttribute('role', 'alert');
			spinnerEl.setAttribute('tabindex', '-1');
			spinnerEl.setAttribute('aria-label', params.spinnerLabelText);
			spinnerEl.setAttribute('class', 'detailedView hasSpinner');
			spinnerEl.setAttribute('style', 'position:absolute;'
				+ 'width:' + (transactionItem.outerWidth() + 2) + 'px;'
				+ 'height:' + transactionItem.outerHeight() + 'px;'
				+ 'top:' + transactionItem.position().top + 'px;'
				+ 'left:' + (transactionItem.position().left - 2) + 'px;'
			);

			return spinnerEl;
		},

		/*
		 * Handle events triggered when inline Transaction Details is done loading and
		 * fully expanded on the UI.
		 *
		 * @param transactionId {String} - The ID of the target transaction
		 * @param isLoading {Boolean} - True or false for loading the target transaction
		 */
		toggleTransactionExpanded: function (transactionId, isExpanded) {
			var detailsModel,
				detailData,
				fptiTag,
				txnModel = this.collection.get(transactionId);

			txnModel.toggleProp('isExpanded', isExpanded);

			// make sure prevent tabbing is disabled after opening/closing animation is done
			this.togglePreventTabbing(false);

			// because the summary row does not have the HTML5 data attributes
			// that the close button has, it does not automatically trigger
			// the FPTI event for td_close, so we'll do it here
			if (!isExpanded) {

				detailsModel = txnModel.get('transactionDetailsModel');
				if (detailsModel) {
					detailData = detailsModel.get('data');
				}
				if (detailData) {
					fptiTag = detailData && detailData.details && detailData.details.fptiTag;
				}
				if (fptiTag) {
					this.trackFPTILink(fptiTag + ':td_close');
				}
			}
		}

	});

	return TransactionCollectionView;
});



//////////////////
// WEBPACK FOOTER
// ./view/activity/transactionCollectionView.js
// module id = 760
// module chunks = 0